"use client";

import { formatCurrency, germanDayjs, priceList } from "@/utils/helpers";
import { captureClientEvent } from "@/lib/telemetry-client";
import { useEffect, useState, useMemo } from "react";
import { BsStars, BsFillSunFill } from "react-icons/bs";
import { FaSpinner, FaCar, FaCaravan, FaMoon, FaSun, FaCreditCard } from "react-icons/fa6";
import { Dayjs } from "dayjs";
import Checkout from "@/components/checkout/checkout";
import { useTicketSettings } from "@/hooks/use-ticket-settings";
import { getOutsideSaleNote } from "@/utils/admin-settings";
import { Sheet } from "./ui/sheet";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Image from "next/image";
import { FaShuttleVan } from "react-icons/fa";

interface TicketCardProps {
    title: string;
    subtitle: string;
    price: string;
    icon: React.ReactNode;
    disabled?: boolean;
    loading?: boolean;
    note?: string | null;
    onClick: () => void;
    buttonText?: string;
    accentColor?: 'amber' | 'slate';
    delay?: number;
    inView?: boolean;
}

function TicketCard({
    title,
    subtitle,
    price,
    icon,
    disabled,
    loading,
    note,
    onClick,
    buttonText = "Jetzt bezahlen",
    accentColor = 'amber',
    delay = 0,
    inView = true
}: TicketCardProps) {
    const isSlate = accentColor === 'slate';
    const isNachzahlungNote = note === "Nachzahlung";
    const isDisabledByNote = Boolean(note) && !isNachzahlungNote;

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay }}
            className="group relative"
        >
            <div className={`
                relative h-full overflow-hidden rounded-3xl p-6
                ${isSlate
                    ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-white'
                    : 'bg-gradient-to-br from-white/90 to-amber-50/90 text-slate-800'
                }
                shadow-xl shadow-amber-900/10 backdrop-blur-sm
                transition-all duration-500
                hover:-translate-y-2 hover:shadow-2xl hover:shadow-amber-900/20
            `}>
                {/* Background decoration */}
                <div className={`
                    absolute -right-8 -top-8 h-32 w-32 rounded-full transition-all duration-500 group-hover:scale-125
                    ${isSlate ? 'bg-amber-400/10' : 'bg-amber-300/30'}
                `} />
                <div className={`
                    absolute -bottom-4 -left-4 h-20 w-20 rounded-full transition-all duration-700 group-hover:scale-150
                    ${isSlate ? 'bg-slate-600/50' : 'bg-amber-200/50'}
                `} />

                {/* Content */}
                <div className="relative z-10 flex h-full flex-col">
                    {/* Icon & Title */}
                    <div className="mb-4 flex items-start justify-between">
                        <div className={`
                            flex h-14 w-14 items-center justify-center rounded-2xl
                            ${isSlate
                                ? 'bg-amber-400/20 text-amber-400'
                                : 'bg-amber-400/30 text-amber-600'
                            }
                            transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3
                        `}>
                            {icon}
                        </div>
                        <div className={`
                            rounded-full px-3 py-1 text-xs font-medium
                            ${isSlate
                                ? 'bg-amber-400/20 text-amber-300'
                                : 'bg-amber-100 text-amber-700'
                            }
                        `}>
                            {isSlate ? 'Tag & Nacht' : 'Tagsüber'}
                        </div>
                    </div>

                    {/* Title */}
                    <h3 className={`
                        mb-1 font-special text-xl font-bold
                        ${isSlate ? 'text-white' : 'text-slate-800'}
                    `}>
                        {title}
                    </h3>

                    {/* Subtitle */}
                    <p className={`
                        mb-4 text-sm
                        ${isSlate ? 'text-slate-400' : 'text-slate-500'}
                    `}>
                        {subtitle}
                    </p>

                    {/* Note */}
                    {isDisabledByNote && (
                        <div className={`
                            mb-4 rounded-xl px-3 py-2 text-xs font-medium
                            ${isSlate
                                ? 'bg-amber-400/15 text-amber-300'
                                : 'bg-amber-50 text-amber-700 border border-amber-200/50'
                            }
                        `}>
                            {note}
                        </div>
                    )}

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Price & Button */}
                    <div className="flex items-end justify-between">
                        <div>
                            <p className={`text-xs ${isSlate ? 'text-slate-500' : 'text-slate-400'}`}>
                                Preis
                            </p>
                            <p className={`
                                font-special text-3xl font-bold
                                ${isSlate ? 'text-amber-400' : 'text-slate-800'}
                            `}>
                                {price}
                            </p>
                        </div>

                        <button
                            onClick={onClick}
                            disabled={disabled || loading || isDisabledByNote}
                            className={`
                                relative overflow-hidden rounded-full px-6 py-3 font-semibold
                                transition-all duration-300
                                disabled:cursor-not-allowed disabled:opacity-50
                                ${isSlate
                                    ? 'bg-amber-400 text-slate-900 hover:bg-amber-300 hover:scale-105'
                                    : 'bg-slate-800 text-white hover:bg-slate-700 hover:scale-105'
                                }
                            `}
                        >
                            {loading ? (
                                <FaSpinner className="h-5 w-5 animate-spin" />
                            ) : (
                                <span>{isDisabledByNote ? note : buttonText}</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function TicketOverview() {
    const [ticket, setTicket] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [now, setNow] = useState<Dayjs | null>(null);
    const prices = priceList();
    const { ticketHours, isTemporaryOverrideActive, isLoading: settingsLoading } = useTicketSettings();

    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    const isLoadingTime = !now || settingsLoading || !ticketHours;
    const currentHour = now?.hour() ?? 0;

    const noteForTicket = useMemo(() => {
        if (isTemporaryOverrideActive || !ticketHours || !now) return {};
        return {
            pkwday: getOutsideSaleNote("pkwday", currentHour, ticketHours),
            four: getOutsideSaleNote("four", currentHour, ticketHours),
            womo: getOutsideSaleNote("womo", currentHour, ticketHours),
            caravan: getOutsideSaleNote("caravan", currentHour, ticketHours),
            pkwnight: getOutsideSaleNote("pkwnight", currentHour, ticketHours),
        };
    }, [isTemporaryOverrideActive, ticketHours, now, currentHour]);

    useEffect(() => {
        setNow(germanDayjs());
        const timer = setInterval(() => {
            setNow(germanDayjs());
        }, 60000);

        return () => clearInterval(timer);
    }, []);

    const handleTicketSelect = (ticketType: string) => {
        setTicket(ticketType);
        setDialogOpen(true);
        captureClientEvent("ticket_type_selected", { ticket_type: ticketType, source: "overview_card" });
    };

    const [weeklyTickets, setWeeklyTickets] = useState<number | null>(null);

    useEffect(() => {
        fetch("/api/ticket-stats")
            .then((res) => res.json())
            .then((data) => {
                if (data.weeklyTickets) {
                    setWeeklyTickets(data.weeklyTickets);
                }
            })
            .catch(() => {
                setWeeklyTickets(null);
            });
    }, []);

    return (
        <div ref={ref} className="mx-auto max-w-6xl px-4">
            {/* Social proof banner */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
                className="mb-8 flex flex-wrap items-center justify-center gap-4"
            >
                {weeklyTickets !== null && weeklyTickets > 0 && (
                    <div className="flex items-center gap-2 rounded-full bg-emerald-500/20 px-4 py-2 text-sm">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                        <span className="text-emerald-700">Bereits {weeklyTickets} Tickets verkauft</span>
                    </div>
                )}
            </motion.div>

            {/* Day tickets section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
                className="mb-6 flex items-center justify-center gap-3"
            >
                <BsFillSunFill className="h-6 w-6 text-amber-600" />
                <h3 className="font-handwritten text-2xl text-slate-700">Tagsüber</h3>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
                <TicketCard
                    title="Tagesticket"
                    subtitle={`Gültig von ${ticketHours?.dayTicketValidFrom ?? 7} bis ${ticketHours?.dayTicketValidUntil ?? 23} Uhr`}
                    price={formatCurrency(prices["pkwday"])}
                    icon={<FaCar className="h-7 w-7" />}
                    loading={isLoadingTime}
                    note={noteForTicket.pkwday}
                    onClick={() => handleTicketSelect("pkwday")}
                    accentColor="amber"
                    delay={0.1}
                    inView={inView}
                />

                <TicketCard
                    title="Nachmittags-Ticket"
                    subtitle={`Gültig ab ${ticketHours?.afternoonValidFrom ?? 14} Uhr · max. 4 Stunden`}
                    price={formatCurrency(prices["four"])}
                    icon={<FaSun className="h-7 w-7" />}
                    loading={isLoadingTime}
                    note={noteForTicket.four}
                    onClick={() => handleTicketSelect("four")}
                    accentColor="amber"
                    delay={0.2}
                    inView={inView}
                />
            </div>

            {/* Night tickets section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-6 mt-12 flex items-center justify-center gap-3"
            >
                <BsStars className="h-6 w-6 text-amber-600" />
                <h3 className="font-handwritten text-2xl text-slate-700">Tag & Nacht</h3>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                <TicketCard
                    title="Womo | Bulli | Dachzelt | PKW"
                    subtitle={`Übernachtung: Gültig bis ${ticketHours?.nightValidUntil ?? 9} Uhr morgens`}
                    price={formatCurrency(prices["womo"])}
                    icon={<FaShuttleVan className="h-7 w-7" />}
                    note={noteForTicket.womo}
                    onClick={() => handleTicketSelect("womo")}
                    buttonText="Jetzt bezahlen"
                    accentColor="slate"
                    delay={0.4}
                    inView={inView}
                />

                <TicketCard
                    title="PKW + Caravan"
                    subtitle={`Übernachtung: Gültig bis ${ticketHours?.nightValidUntil ?? 9} Uhr morgens`}
                    price={formatCurrency(prices["caravan"])}
                    icon={<FaCaravan className="h-7 w-7" />}
                    note={noteForTicket.caravan}
                    onClick={() => handleTicketSelect("caravan")}
                    accentColor="slate"
                    delay={0.5}
                    inView={inView}
                />

                <TicketCard
                    title="PKW Abstellen"
                    subtitle="ohne Übernachtung"
                    price={formatCurrency(prices["pkwnight"] * quantity)}
                    icon={<FaCar className="h-7 w-7" />}
                    note={noteForTicket.pkwnight}
                    onClick={() => handleTicketSelect("pkwnight")}
                    accentColor="slate"
                    delay={0.6}
                    inView={inView}
                />
            </div>

            {/* Payment methods */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="mt-12 flex flex-col items-center gap-3"
            >
                <p className="text-sm text-slate-500">Sichere Bezahlung mit</p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                    <div className="flex items-center gap-2 rounded-lg bg-white/80 px-3 py-2 shadow-sm">
                        <FaCreditCard className="h-5 w-5 text-slate-600" />
                        <span className="text-sm font-medium text-slate-700">Kreditkarte</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-white/80 px-3 py-2 shadow-sm">
                        <svg className="h-5 w-5" viewBox="7.056000232696533 3 37.35095977783203 45"><g xmlns="http://www.w3.org/2000/svg" clipPath="url(#paypal__a)"><path fill="#002991" d="M38.914 13.35c0 5.574-5.144 12.15-12.927 12.15H18.49l-.368 2.322L16.373 39H7.056l5.605-36h15.095c5.083 0 9.082 2.833 10.555 6.77a9.687 9.687 0 0 1 .603 3.58z" /><path fill="#60CDFF" d="M44.284 23.7A12.894 12.894 0 0 1 31.53 34.5h-5.206L24.157 48H14.89l1.483-9 1.75-11.178.367-2.322h7.497c7.773 0 12.927-6.576 12.927-12.15 3.825 1.974 6.055 5.963 5.37 10.35z" /><path fill="#008CFF" d="M38.914 13.35C37.31 12.511 35.365 12 33.248 12h-12.64L18.49 25.5h7.497c7.773 0 12.927-6.576 12.927-12.15z" /></g></svg>
                        <span className="text-sm font-medium text-slate-700">PayPal</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-white/80 px-3 py-2 shadow-sm">
                        <svg className="h-5 w-5 stroke-pink-300 fill-pink-300" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <title>Klarna</title>
                            <path d="M4.592 2v20H0V2h4.592zm11.46 0c0 4.194-1.583 8.105-4.415 11.068l-.278.283L17.702 22h-5.668l-6.893-9.4 1.779-1.332c2.858-2.14 4.535-5.378 4.637-8.924L11.562 2h4.49zM21.5 17a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" />
                        </svg>
                        <span className="text-sm font-medium text-slate-700">Klarna</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-white/80 px-3 py-2 shadow-sm">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                            <rect width="24" height="24" rx="4" fill="#1A1F71" />
                            <path d="M4 12c0-4.4 3.6-8 8-8s8 3.6 8 8-3.6 8-8 8-8-3.6-8-8z" fill="#EA001B" />
                            <path d="M12 4c4.4 0 8 3.6 8 8s-3.6 8-8 8" fill="#F79E1B" />
                        </svg>
                        <span className="text-sm font-medium text-slate-700">Giropay</span>
                    </div>
                </div>
                <p className="text-xs font-medium text-slate-500">
                    Alle Preise zzgl. {formatCurrency(prices["card"])} Transaktionsgebühr pro Zahlung
                </p>
            </motion.div>

            <Sheet
                open={dialogOpen}
                onOpenChange={(open) => {
                    setDialogOpen(open);
                    captureClientEvent(open ? "checkout_sheet_opened" : "checkout_sheet_closed", {
                        ticket_type: ticket || null,
                    });
                }}
            >
                <Checkout ticket={ticket} quantities={quantity} onOpen={dialogOpen} />
            </Sheet>
        </div>
    );
}
